/* ---------------------------------------------------------------------
   "Notify me when it's back" button.

   Replaces the disabled Add to cart on any product page where the SELECTED
   variant is out of stock, with a WhatsApp link carrying the message the
   waitlist automation listens for:

       NOTIFY <sku>

   The automation matches the inbound text against a pattern anchored to
   end-of-string, and does not trim first. So the message must be one line
   with nothing after the SKU - a single trailing newline makes it fail to
   match, and it fails silently: the customer sends it and never hears back.
   Nothing here appends to the message for that reason.

   Two product templates are in play and this handles both without knowing
   which it is on: the stock main-product section used by apparel, and the
   custom hype-sets buy box used by bags, bottles and accessories. It finds
   the real product form by matching the hidden id input against this
   product's own variant ids, which also stops it latching onto the forms
   inside related-product cards.

   Variant data comes from /products/<handle>.js rather than the DOM, so it
   does not depend on either section's markup. Apparel re-renders its buy
   buttons on every size change, so the sync re-queries the button each pass
   rather than holding a reference.
--------------------------------------------------------------------- */

(function () {
  'use strict';

  var WHATSAPP_NUMBER = '971545809511'; // WhatsApp Business number the automation runs on
  var BUTTON_LABEL = "Notify me when it's back";

  var byId = null; // variant id -> { sku, available }

  function productHandle() {
    var parts = window.location.pathname.split('/products/');
    if (parts.length < 2) return null;
    return parts[1].split('/')[0].split('?')[0].split('#')[0] || null;
  }

  function loadVariants(done) {
    var handle = productHandle();
    if (!handle) return;

    fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (product) {
        if (!product || !product.variants) return;
        byId = {};
        product.variants.forEach(function (v) {
          byId[String(v.id)] = { sku: v.sku || '', available: !!v.available };
        });
        done();
      })
      .catch(function () { /* leave the page exactly as the theme rendered it */ });
  }

  /* The product form is the one whose id input holds a variant of THIS
     product. Related-product cards carry their own add-to-cart forms. */
  function findForm() {
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    for (var i = 0; i < forms.length; i++) {
      var input = forms[i].querySelector('[name="id"]');
      if (input && byId[String(input.value)]) return forms[i];
    }
    return null;
  }

  function addToCartButton(form) {
    return form.querySelector('button[type="submit"], input[type="submit"]');
  }

  function waLink(sku) {
    // one line, nothing after the SKU
    return 'https://wa.me/' + WHATSAPP_NUMBER + '?text=' + encodeURIComponent('NOTIFY ' + sku);
  }

  /* WhatsApp mark, so it is obvious the button opens WhatsApp rather than
     collecting an email. */
  var WHATSAPP_ICON =
    '<svg width="19" height="19" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
    '<path d="M17.47 14.38c-.3-.15-1.75-.86-2.02-.96-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.64.08-.3-.15-1.25-.46-2.38-1.47-.88-.78-1.47-1.75-1.64-2.05-.17-.3-.02-.46.13-.6.13-.14.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.6-.92-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.01-1.04 2.47 0 1.46 1.06 2.87 1.21 3.07.15.2 2.09 3.2 5.07 4.49.71.3 1.26.49 1.69.63.71.22 1.36.19 1.87.12.57-.09 1.75-.72 2-1.41.25-.69.25-1.28.17-1.41-.07-.13-.27-.2-.57-.35Z"></path>' +
    '<path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.87 9.87 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2Zm0 18.13h-.01a8.2 8.2 0 0 1-4.18-1.15l-.3-.18-3.11.82.83-3.04-.2-.31a8.17 8.17 0 0 1-1.25-4.36c0-4.53 3.69-8.22 8.23-8.22 2.2 0 4.26.86 5.81 2.41a8.16 8.16 0 0 1 2.41 5.82c0 4.54-3.7 8.21-8.23 8.21Z"></path>' +
    '</svg>';

  function buildButton() {
    var a = document.createElement('a');
    a.className = 'hype-bis-button';
    a.target = '_blank';
    a.rel = 'noopener';
    a.setAttribute('data-hype-bis', '');
    a.innerHTML = WHATSAPP_ICON + '<span>' + BUTTON_LABEL + '</span>';
    return a;
  }

  function sync() {
    if (!byId) return;

    var form = findForm();
    if (!form) return;

    var input = form.querySelector('[name="id"]');
    var variant = input ? byId[String(input.value)] : null;
    var atc = addToCartButton(form);
    var existing = form.querySelector('[data-hype-bis]');

    /* Show only when the selected variant is genuinely out of stock AND has a
       SKU. A variant with no SKU would produce "NOTIFY " with nothing after
       it, which cannot match - better no button than a dead one. */
    var wanted = variant && !variant.available && variant.sku && variant.sku.length >= 2;

    if (!wanted) {
      if (existing) existing.remove();
      if (atc) atc.classList.remove('hype-bis-replaced');
      return;
    }

    if (!atc) return;

    if (existing) {
      var href = waLink(variant.sku);
      if (existing.getAttribute('href') !== href) existing.setAttribute('href', href);
    } else {
      var button = buildButton();
      button.setAttribute('href', waLink(variant.sku));
      atc.parentNode.insertBefore(button, atc.nextSibling);
    }

    atc.classList.add('hype-bis-replaced');
  }

  function start() {
    sync();

    /* The theme swaps the buy buttons out when the size changes, so watch for
       it rather than binding to elements that get replaced. Coalesced to one
       pass per frame. */
    var queued = false;
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        sync();
      });
    }).observe(document.body, { childList: true, subtree: true });

    document.addEventListener('change', sync, true);
    window.addEventListener('popstate', sync);
  }

  function boot() {
    if (!document.body) {
      setTimeout(boot, 50);
      return;
    }
    loadVariants(start);
  }

  boot();
})();
